import { useContext, useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router";
import { motion } from "framer-motion";
import {
  Box, Sheet, Stack, Typography, Chip, Button, Divider,
  CircularProgress, Snackbar,
} from "@mui/joy";
import CreditCardRoundedIcon from "@mui/icons-material/CreditCardRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import HourglassEmptyRoundedIcon from "@mui/icons-material/HourglassEmptyRounded";
import SyncRoundedIcon from "@mui/icons-material/SyncRounded";
import PaymentRoundedIcon from "@mui/icons-material/PaymentRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";

import { LayoutContext } from "../../context";
import { usePermissions } from "../../usePermissions";
import { APIClientGetPayments, APIClientPayApplication, APIClientCheckPayment } from "../../api";

const PAY_STATUS_CFG = {
  created:  { label: "Ожидает оплаты", color: "warning" },
  pending:  { label: "В обработке",    color: "warning" },
  paid:     { label: "Оплачено",       color: "success" },
  failed:   { label: "Ошибка оплаты",  color: "danger"  },
  expired:  { label: "Истёк срок",     color: "neutral" },
  refunded: { label: "Возврат",        color: "neutral" },
  canceled: { label: "Отменён",        color: "neutral" },
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("ru-RU", {
      day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

function fmtMoney(amount, currency = "RUB") {
  const n = Number(amount);
  if (Number.isNaN(n)) return `${amount} ${currency}`;
  return `${n.toLocaleString("ru-RU")} ${currency === "RUB" ? "₽" : currency}`;
}

function PaymentRow({ payment, onChanged, onToast }) {
  const [payLoading, setPayLoading] = useState(false);
  const [checkLoading, setCheckLoading] = useState(false);
  const cfg = PAY_STATUS_CFG[payment.status] || PAY_STATUS_CFG.created;
  const label = payment.status_display || cfg.label;

  const handlePay = async () => {
    if (payment.payment_url) {
      window.open(payment.payment_url, "_blank", "noopener");
      return;
    }
    setPayLoading(true);
    const res = await APIClientPayApplication(payment.application_id);
    setPayLoading(false);
    if (res?.payment?.payment_url) {
      window.open(res.payment.payment_url, "_blank", "noopener");
      onChanged?.();
    } else {
      onToast?.(res?.detail || "Не удалось получить ссылку на оплату", "danger");
    }
  };

  const handleCheck = async () => {
    setCheckLoading(true);
    const res = await APIClientCheckPayment(payment.id);
    setCheckLoading(false);
    if (res?.id) {
      onToast?.(res.is_paid ? "Оплата получена!" : `Статус: ${res.status_display || res.status}`,
                res.is_paid ? "success" : "neutral");
      onChanged?.();
    } else {
      onToast?.(res?.detail || "Не удалось проверить платёж", "danger");
    }
  };

  return (
    <Sheet variant="outlined" sx={{ borderRadius: "16px", p: 2, mb: 1.5 }}>
      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between"
             alignItems={{ xs: "flex-start", sm: "center" }} spacing={1.5}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0 }}>
          <Sheet variant="soft" color={cfg.color}
                 sx={{ width: 44, height: 44, borderRadius: "12px", display: "flex",
                       alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <ReceiptLongRoundedIcon />
          </Sheet>
          <Box sx={{ minWidth: 0 }}>
            <Typography level="title-sm" fontWeight={700}>{fmtMoney(payment.amount, payment.currency)}</Typography>
            <Typography level="body-xs" color="neutral" sx={{ mt: 0.3 }}>
              {payment.application_id ? `Заявка #${payment.application_id}` : "—"}
              {payment.application_address ? ` · ${payment.application_address}` : ""}
            </Typography>
            <Typography level="body-xs" color="neutral" sx={{ opacity: 0.7, mt: 0.2 }}>
              Создан {fmtDate(payment.date_create)}{payment.paid_at ? ` · Оплачен ${fmtDate(payment.paid_at)}` : ""}
            </Typography>
          </Box>
        </Stack>

        <Stack direction={{ xs: "row", sm: "column" }} alignItems={{ xs: "center", sm: "flex-end" }}
               spacing={1} sx={{ flexShrink: 0, width: { xs: "100%", sm: "auto" }, justifyContent: "space-between" }}>
          <Chip variant="solid" color={cfg.color} size="sm"
                startDecorator={payment.is_paid ? <CheckCircleRoundedIcon sx={{ fontSize: 14 }} />
                                                 : <HourglassEmptyRoundedIcon sx={{ fontSize: 14 }} />}>
            {label}
          </Chip>

          <Stack direction="row" spacing={1}>
            {payment.is_payable && (
              <Button size="sm" variant="solid" color="primary" loading={payLoading}
                      startDecorator={<PaymentRoundedIcon />} onClick={handlePay}>
                Оплатить
              </Button>
            )}
            {!payment.is_paid && (
              <Button size="sm" variant="soft" color="neutral" loading={checkLoading}
                      startDecorator={<SyncRoundedIcon />} onClick={handleCheck}>
                Проверить
              </Button>
            )}
          </Stack>
        </Stack>
      </Stack>
    </Sheet>
  );
}

export default function PaymentsPage() {
  const { account } = useContext(LayoutContext);
  const navigate = useNavigate();
  const { hasAnyPermission } = usePermissions();
  const canView = hasAnyPermission("view_payments", "manage_payments");

  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ open: false, msg: "", color: "neutral" });

  const showToast = useCallback((msg, color = "neutral") => setToast({ open: true, msg, color }), []);

  const load = useCallback(async () => {
    setLoading(true);
    const data = await APIClientGetPayments();
    if (Array.isArray(data)) setPayments(data);
    setLoading(false);
  }, []);

  useEffect(() => { if (canView) load(); else setLoading(false); }, [canView, load]);

  if (!canView) {
    return (
      <Box sx={{ maxWidth: 1200, mx: "auto", px: { xs: 2, sm: 3 }, py: 3, pb: 8 }}>
        <Sheet variant="soft" color="warning" sx={{ borderRadius: "20px", p: 6, textAlign: "center" }}>
          <Typography level="title-md" fontWeight={700}>Нет доступа к платежам</Typography>
          <Typography level="body-sm" color="neutral" sx={{ mt: 0.5 }}>
            Обратитесь к руководителю аккаунта, чтобы получить право «Платежи».
          </Typography>
          <Button sx={{ mt: 2 }} variant="soft" onClick={() => navigate("/lk")}>К заявкам</Button>
        </Sheet>
      </Box>
    );
  }

  const stats = {
    total: payments.length,
    paid: payments.filter(p => p.status === "paid").length,
    awaiting: payments.filter(p => ["created", "pending"].includes(p.status)).length,
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", px: { xs: 2, sm: 3 }, py: 3, pb: 8 }}>
      <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.32 }}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
          <Sheet variant="soft" color="primary"
                 sx={{ width: 44, height: 44, borderRadius: "12px", display: "flex",
                       alignItems: "center", justifyContent: "center" }}>
            <CreditCardRoundedIcon />
          </Sheet>
          <Box>
            <Typography level="h4" fontWeight={800}>Платежи</Typography>
            <Typography level="body-sm" color="neutral">Оплата услуг и история платежей</Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={1.5} sx={{ mb: 2 }}>
          {[
            { label: "Всего", value: stats.total, color: "primary" },
            { label: "Оплачено", value: stats.paid, color: "success" },
            { label: "Ожидают", value: stats.awaiting, color: "warning" },
          ].map(s => (
            <Sheet key={s.label} variant="soft" color={s.color}
                   sx={{ borderRadius: "16px", p: 2, flex: 1, textAlign: "center" }}>
              <Typography level="h3" fontWeight={800}>{s.value}</Typography>
              <Typography level="body-xs" fontWeight={600}>{s.label}</Typography>
            </Sheet>
          ))}
        </Stack>

        <Divider sx={{ mb: 2 }} />

        {loading ? (
          <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress /></Stack>
        ) : payments.length === 0 ? (
          <Sheet variant="soft" color="neutral" sx={{ borderRadius: "20px", p: 6, textAlign: "center" }}>
            <Typography level="title-md" fontWeight={700}>Платежей пока нет</Typography>
            <Typography level="body-sm" color="neutral" sx={{ mt: 0.5 }}>
              Платёж создаётся автоматически, когда заявка готова к оплате.
            </Typography>
          </Sheet>
        ) : (
          payments.map(p => (
            <PaymentRow key={p.id} payment={p} onChanged={load} onToast={showToast} />
          ))
        )}
      </motion.div>

      <Snackbar open={toast.open} autoHideDuration={3500} color={toast.color} variant="soft"
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                onClose={() => setToast(t => ({ ...t, open: false }))}>
        {toast.msg}
      </Snackbar>
    </Box>
  );
}